import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, quantitySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { deliveryCompareBasisSchema } from "./settings.js";
import { millimetreSchema, temperatureSchema } from "./readings.js";
/**
 * Tanker dolumu (Prompt 12 §3).
 *
 *   ölçülen teslim = (sonrası hacim − öncesi hacim) + boşaltma sırasında o tanktan yapılan satış
 *
 * Karşılaştırma tabanı (ortam / 15 °C) ve tolerans ayardır ve ÖLÇÜM ANINDA belgeye dondurulur: ayar sonradan
 * değişse de onaylı belge değişmez. Dolum MUHASEBE FİŞİ ÜRETMEZ (stok girişi alış faturasınındır); yalnızca
 * stok alt defterine (`fuel_stock_costs`) miktar ve maliyet işler.
 */
export const DELIVERY_STATUSES = [
    "PLANNED",
    "ARRIVED",
    "UNLOADING",
    "MEASURED",
    "CONFIRMED",
    "DISPUTED",
    "CANCELLED",
];
export const deliveryStatusSchema = z.enum(DELIVERY_STATUSES);
export const DELIVERY_STATUS_LABELS = {
    PLANNED: "Planlandı",
    ARRIVED: "Tanker geldi",
    UNLOADING: "Boşaltılıyor",
    MEASURED: "Ölçüldü",
    CONFIRMED: "Onaylandı",
    DISPUTED: "İtiraz edildi",
    CANCELLED: "İptal edildi",
};
export const DELIVERY_SALES_SOURCES = ["TOTALIZER", "TRANSACTIONS", "MANUAL"];
export const deliverySalesSourceSchema = z.enum(DELIVERY_SALES_SOURCES);
export const DELIVERY_SALES_SOURCE_LABELS = {
    TOTALIZER: "Tabanca endeksinden",
    TRANSACTIONS: "Satış işlemlerinden",
    MANUAL: "Elle girildi",
};
export const DELIVERY_COST_STATUSES = ["NONE", "PROVISIONAL", "FINAL"];
export const deliveryCostStatusSchema = z.enum(DELIVERY_COST_STATUSES);
export const DELIVERY_COST_STATUS_LABELS = {
    NONE: "Maliyetlendirilmedi",
    PROVISIONAL: "Geçici (fatura bekleniyor)",
    FINAL: "Kesin (faturadan)",
};
// ── Girdi ────────────────────────────────────────────────────────────────────
export const deliveryLineInputSchema = z.object({
    productId: uuidSchema,
    documentL: quantitySchema.refine((value) => Number(value) > 0, "İrsaliye miktarı sıfırdan büyük olmalıdır"),
    documentL15c: quantitySchema.nullable().optional().describe("İrsaliyedeki 15 °C litre"),
    documentTemperatureC: temperatureSchema.nullable().optional(),
    documentDensity: z
        .string()
        .regex(/^\d\.\d{1,5}$/, "Yoğunluk 0,x biçiminde olmalıdır")
        .nullable()
        .optional(),
    tankIds: z.array(uuidSchema).min(1).max(8).describe("Bu ürünün boşaltıldığı tank(lar)"),
});
const deliveryLinesInput = z
    .array(deliveryLineInputSchema)
    .min(1)
    .max(20)
    .refine((lines) => new Set(lines.map((line) => line.productId)).size === lines.length, "Aynı ürün iki satırda olamaz");
export const createDeliverySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    orderId: uuidSchema.nullable().optional().describe("Verilirse dağıtıcı ve satırlar siparişten önerilir"),
    distributorCounterpartyId: uuidSchema.optional().describe("Sipariş verilmişse zorunlu değil"),
    despatchNo: z.string().trim().max(40).nullable().optional(),
    despatchDate: localDateSchema.nullable().optional(),
    truckPlate: z.string().trim().max(20).nullable().optional(),
    trailerPlate: z.string().trim().max(20).nullable().optional(),
    /** KVKK: kişisel veri — olaya ve günlüğe YAZILMAZ, yalnızca belgede ve itiraz tutanağında görünür. */
    driverName: z.string().trim().max(150).nullable().optional(),
    sealNumbers: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    lines: deliveryLinesInput.optional().describe("Verilmezse siparişten kopyalanır"),
});
/** PATCH: yalnızca PLANNED / ARRIVED iken; `lines` verilirse satırlar TAMAMEN değişir. */
export const updateDeliverySchema = patchOf(createDeliverySchema.omit({ companyId: true, stationId: true, orderId: true })).extend({ version: z.number().int().min(1) });
/** Dolum geçişlerinde okunan tank ölçümü; verilmezse en yakın prob okuması kullanılır. */
export const deliveryTankReadingInputSchema = z.object({
    tankId: uuidSchema,
    measuredAt: timestampSchema,
    levelMm: millimetreSchema,
    waterMm: millimetreSchema.nullable().optional(),
    temperatureC: temperatureSchema.nullable().optional(),
});
const versionOnly = { version: z.number().int().min(1) };
/**
 * Geçişin GERÇEKLEŞTİĞİ an. Verilmezse "şimdi"; gelecekte olamaz ve önceki geçişin anından önce olamaz.
 * Demo verisi ve istasyon ajanı geçmişe dönük dolum yazabilsin diye vardır; oturma süresi denetimi de
 * bu zamanla yapılır (duvar saatiyle değil).
 */
const atField = { at: timestampSchema.optional().describe("Geçişin gerçekleştiği an (varsayılan: şimdi)") };
export const arriveDeliverySchema = z.object({
    ...versionOnly,
    ...atField,
    readings: z.array(deliveryTankReadingInputSchema).max(16).optional(),
});
export const startUnloadingSchema = z.object({ ...versionOnly, ...atField });
/** Boşaltmanın bittiği an — oturma süresi buradan sayılır. Durum UNLOADING kalır. */
export const finishUnloadingSchema = z.object({ ...versionOnly, ...atField });
export const manualUnloadSalesSchema = z.object({ tankId: uuidSchema, litres: quantitySchema });
export const measureDeliverySchema = z.object({
    ...versionOnly,
    ...atField,
    unloadingFinishedAt: timestampSchema
        .optional()
        .describe("Boşaltma bitiş anı henüz yazılmadıysa burada verilebilir"),
    readings: z.array(deliveryTankReadingInputSchema).max(16).optional(),
    /** Endeks ve işlem verisi yoksa boşaltma sırasındaki satış elle girilir. */
    salesDuringUnload: z.array(manualUnloadSalesSchema).max(16).optional(),
    force: z.boolean().optional().describe("Oturma süresi dolmadan ölçmek için"),
    forceReason: z.string().trim().min(3).max(500).optional(),
});
export const confirmDeliverySchema = z.object({
    ...versionOnly,
    ...atField,
    note: z.string().trim().max(1000).optional(),
});
export const disputeDeliverySchema = z.object({
    ...versionOnly,
    ...atField,
    reason: z.string().trim().min(3).max(1000),
});
export const cancelDeliverySchema = z.object({
    ...versionOnly,
    reason: z.string().trim().min(3).max(500),
});
export const matchDeliveryInvoiceSchema = z.object({ invoiceId: uuidSchema });
// ── Çıktı ────────────────────────────────────────────────────────────────────
export const deliveryLineTankSchema = z.object({
    id: uuidSchema,
    tankId: uuidSchema,
    tankNo: z.number().int(),
    beforeAt: timestampSchema.nullable(),
    beforeLevelMm: millimetreSchema.nullable(),
    beforeWaterMm: millimetreSchema.nullable(),
    beforeTemperatureC: temperatureSchema.nullable(),
    beforeVolumeL: quantitySchema.nullable(),
    beforeVolumeL15c: quantitySchema.nullable(),
    afterAt: timestampSchema.nullable(),
    afterLevelMm: millimetreSchema.nullable(),
    afterWaterMm: millimetreSchema.nullable(),
    afterTemperatureC: temperatureSchema.nullable(),
    afterVolumeL: quantitySchema.nullable(),
    afterVolumeL15c: quantitySchema.nullable(),
    salesDuringUnloadL: quantitySchema.nullable(),
    salesSource: deliverySalesSourceSchema.nullable(),
    measuredL: quantitySchema.nullable(),
});
export const deliveryLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    productId: uuidSchema,
    productName: z.string(),
    documentL: quantitySchema,
    documentL15c: quantitySchema.nullable(),
    documentTemperatureC: temperatureSchema.nullable(),
    documentDensity: z.string().nullable(),
    measuredL: quantitySchema.nullable(),
    measuredL15c: quantitySchema.nullable(),
    differenceL: quantitySchema.nullable(),
    differencePercent: rateSchema.nullable(),
    toleranceL: quantitySchema.nullable(),
    withinTolerance: z.boolean().nullable(),
    invoiceLineId: uuidSchema.nullable(),
    unitCost: z.string().nullable().describe("₺/L, 6 ondalık"),
    costAmount: moneySchema.nullable(),
    tanks: z.array(deliveryLineTankSchema),
});
export const deliverySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    deliveryNo: z.string().describe("DL{yıl}{6 hane}"),
    orderId: uuidSchema.nullable(),
    orderNo: z.string().nullable(),
    distributorCounterpartyId: uuidSchema,
    distributorName: z.string(),
    despatchNo: z.string().nullable(),
    despatchDate: localDateSchema.nullable(),
    truckPlate: z.string().nullable(),
    trailerPlate: z.string().nullable(),
    driverName: z.string().nullable(),
    sealNumbers: z.array(z.string()),
    status: deliveryStatusSchema,
    businessDate: localDateSchema.nullable(),
    arrivedAt: timestampSchema.nullable(),
    unloadingStartedAt: timestampSchema.nullable(),
    unloadingFinishedAt: timestampSchema.nullable(),
    measuredAt: timestampSchema.nullable(),
    confirmedAt: timestampSchema.nullable(),
    confirmedBy: uuidSchema.nullable(),
    compareBasis: deliveryCompareBasisSchema.nullable(),
    tolerancePerMille: rateSchema.nullable(),
    toleranceMinL: quantitySchema.nullable(),
    withinTolerance: z.boolean().nullable(),
    disputeReason: z.string().nullable(),
    disputedAt: timestampSchema.nullable(),
    disputeFileId: uuidSchema.nullable(),
    purchaseInvoiceId: uuidSchema.nullable(),
    costStatus: deliveryCostStatusSchema,
    note: z.string().nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
    lines: z.array(deliveryLineSchema),
    /** Ölçüm sonucunda tolerans aşıldıysa arayüz itirazı önerir. */
    suggestDispute: z.boolean(),
    /** Ölçümde 15 °C tabanı istenip de veri eksik olduğu için ortama düşüldüyse uyarı. */
    warnings: z.array(z.string()),
});
export const listDeliveriesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    status: deliveryStatusSchema.optional(),
    distributorCounterpartyId: uuidSchema.optional(),
    orderId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const deliveryPageSchema = pageOf(deliverySchema);
export const deliveryParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=deliveries.js.map